import { Box3, Mesh, MeshBasicMaterial, Object3D, SphereGeometry, Vector3 } from "three";
import { ICollidable } from "../../../../../three/physics/ICollidable";
import { GameViewModel } from "../../../models/GameViewModel";
import { ElectricityParticle } from "./particles/ElectricityParticle";
import { BatchedRenderer } from "three.quarks";
import { GameResourcesPackage } from "../../../resources/GameResourcesPackage";
import { BaseMole } from "./mole/BaseMole";
import { AudioController, Sfx } from "../../AudioController";
import { Sound } from "@pixi/sound";
import { PhysicsLayers } from "../../../../../PhysicsLayers";
import { Physics } from "../../../../../three/physics/Physics";
import { Ticker } from "pixi.js";
import { Event } from "event-system";
import { EnergyBoosterData } from "../../../../../common/models/data/EnergyBoosterData";

export class EnergyBooster extends Object3D implements ICollidable {
    public readonly onDestroy: Event;

    private _hitCount: number = 0;
    private get hitCount(): number {
        return this._hitCount;
    }
    private set hitCount(value: number) {
        this._hitCount = value;
        if (this._hitCount >= this.config.maxTargetsCount) {
            this.deactivated = true;
        } else {
            this.findNextTargetPoint();
        }
    }

    private mesh: Mesh;
    private readonly _boundingBox: Box3;
    public get boundingBox(): Box3 {
        return this._boundingBox.setFromObject(this);
    }
    public get collidesWithLayer(): number {
        return 1 << PhysicsLayers.Layer1 | 1 << PhysicsLayers.Layer2;
    }

    private particle: ElectricityParticle;
    private particleSound: Sound;
    private targetPoint: Vector3;
    private velocity: Vector3;
    private deactivated: boolean = false;

    public constructor(
        private readonly gameResources: GameResourcesPackage,
        private readonly audioController: AudioController,
        private readonly particlesBatchRenderer: BatchedRenderer,
        private readonly gameViewModel: GameViewModel,
        private readonly config: EnergyBoosterData,
        initialCoordinates: Vector3
    ) {
        super();
        this.position.copy(initialCoordinates);
        this.velocity = new Vector3(1, 1, 1).multiplyScalar(0.2 * this.config.speed);
        this.onDestroy = new Event();
        const geometry = new SphereGeometry(0.5, 16, 16);
        const material = new MeshBasicMaterial({ visible: false });
        this.mesh = new Mesh(geometry, material);
        this.add(this.mesh);
        this._boundingBox = new Box3().setFromObject(this.mesh);
        this.particleSound = this.audioController.getSfxLoopInstance(Sfx.Electricity);
        this.particle = new ElectricityParticle(this.gameResources, this.particlesBatchRenderer);
        this.add(this.particle);
        this.targetPoint = new Vector3();
        this.findNextTargetPoint();
        this.particleSound.play();
        Physics.add(this);
        Ticker.shared.add(this.onTick, this);
    }

    private onTick(dt: number): void {
        if (!this.targetPoint) return;

        if (this.deactivated) {
            Ticker.shared.remove(this.onTick, this);
            this.destroy();
        }

        const direction = new Vector3().subVectors(this.targetPoint, this.position);
        const distanceToTarget = direction.length();

        // If already close enough -> stop
        if (distanceToTarget < 0.01) {
            this.position.copy(this.targetPoint);
            this.findNextTargetPoint();
            return;
        }
        direction.normalize();

        const speed = this.velocity.length(); // use magnitude of velocity as speed
        const movement = new Vector3().copy(direction).multiplyScalar(speed * dt);

        // If next step overshoots, just clamp to target
        if (movement.length() > distanceToTarget) {
            this.position.copy(this.targetPoint);
        } else {
            this.position.add(movement);
        }

        this._boundingBox.setFromObject(this.mesh);
    }

    private findNextTargetPoint(): void {
        const moles = this.gameViewModel.aliveSpawnedMoles;
        if (moles.length === 0) {
            this.deactivated = true;
            return;
        }

        const fieldsIndicesExceptCurrent = moles.map(mole => mole.fieldIndex);
        const nextIndex = this.gameViewModel.getNearestIndexToCoordinates(this.position, fieldsIndicesExceptCurrent);

        if (nextIndex !== -1) {
            this.targetPoint = this.gameViewModel.indexToGameCoords(nextIndex);
        } else {
            this.deactivated = true;
        }
    }

    public onCollision(other: ICollidable): void {
        if (other instanceof BaseMole && other.alive) {
            other.takeDamage(this.config.damage);
            this.hitCount++;
        }
    }

    public destroy(): void {
        Physics.remove(this);
        Ticker.shared.remove(this.onTick, this);

        this.removeFromParent();

        this.particleSound.stop();
        this.particleSound = null;
        this.particle.destroy();

        this.traverse((child) => {
            if ((child as Mesh).geometry) {
                (child as Mesh).geometry.dispose();
            }

            if ((child as Mesh).material) {
                const material = (child as Mesh).material;
                if (Array.isArray(material)) {
                    material.forEach((mat) => mat.dispose());
                } else {
                    material.dispose();
                }
            }
        });

        this.onDestroy.emit();
    }
}
